import { useRef, useState, useEffect } from "react";
import { FaBars, FaTimes, FaSignOutAlt, FaPhone, FaEnvelope, FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from 'axios';
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/Auth";
import "../header/Header.css";

const API_BASE_URL = 'https://test2.planfix-to-syrve.com';

function Header() {
    const navRef = useRef();
    const { user, logout } = useAuth();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState("Користувач");
    const [dealerName, setDealerName] = useState("");
    const [telegramLink, setTelegramLink] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [translations, setTranslations] = useState({
        conv: '',
        suppliers: '',
        about: 'Про нас',
        history: 'Історія',
        profile: 'Профіль',
        exit: 'Вихід'
    });
    const [language, setLanguage] = useState('uk-UA');
    const [loading, setLoading] = useState(true);
    const [generalError, setGeneralError] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [additionalInfoOpen, setAdditionalInfoOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [userRole, setUserRole] = useState("Черговий");
    const [autoLogoutTriggered, setAutoLogoutTriggered] = useState(false);

    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const checkAuth = () => {
            const hasToken = !!localStorage.getItem('refreshToken');
            const hasUser = !!user?.user_id;
            setIsLoggedIn(hasToken || hasUser);
        };
        
        checkAuth();
        
        const handleStorageChange = (e) => {
            if (e.key === 'refreshToken' || e.key === 'accessToken') {
                checkAuth();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [user]);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/logo`, {
                    responseType: 'blob'
                });
                const imageUrl = URL.createObjectURL(response.data);
                setLogoUrl(imageUrl);
            } catch (error) {
                console.error('Error loading logo:', error);
                setLogoUrl('/logo.png');
            }
        };
        
        if (isLoggedIn) {
            fetchLogo();
        }
        
        return () => {
            if (logoUrl && logoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(logoUrl);
            }
        };
    }, [isLoggedIn]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchUserRole = async (userId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/user-role/${userId}`);
            if (response?.data?.role) {
                setUserRole(response.data.role);
                console.log(`Роль користувача: ${response.data.role}`);
            }
        } catch (error) {
            console.error('Error fetching user role:', error);
            setUserRole("Черговий");
        }
    };

    const fetchContractorData = async (userId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/user-contractor/${userId}`);
            if (response?.data?.name) {
                setDealerName(response.data.name);
                return true;
            } else {
                console.warn('Contractor not found for user:', userId);
                return false;
            }
        } catch (error) {
            console.error('Error fetching contractor data:', error);
            return false;
        }
    };

    const checkContractorExists = async (userId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/user-contractor/${userId}`);
            return response?.data?.name ? true : false;
        } catch (error) {
            return false;
        }
    };

    const handleAutoLogout = async () => {
        if (autoLogoutTriggered) return;
        
        console.log('Auto logout triggered - no contractor found');
        setAutoLogoutTriggered(true);
        
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userTheme');
        
        setUsername("Користувач");
        setDealerName("");
        setUserRole("Черговий");
        setIsLoggedIn(false);
        
        await logout();
        navigate('/Login');
    };

    const setDarkTheme = () => {
        const darkThemeClass = 'dark-theme';
        document.body.className = darkThemeClass;
        localStorage.setItem('userTheme', 'dark');
        console.log('Тема примусово встановлена на dark');
    };

    useEffect(() => {
        setDarkTheme();
        return () => {};
    }, []);

    const fetchThemeConfig = async (userId) => {
        setDarkTheme();
        return;
    };

    const updateThemeConfig = async (newTheme) => {
        console.log('Зміна теми заблокована - завжди темна тема');
        setDarkTheme();
    };

    const toggleTheme = () => {
        console.log('Зміна теми заблокована - завжди темна тема');
        setDarkTheme();
    };

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('refreshToken');
            if (!token && !user?.user_id) {
                setLoading(false);
                setIsLoggedIn(false);
                return;
            }
            
            if (user?.user_id && !autoLogoutTriggered) {
                try {
                    const userId = user.user_id;
                    const cleanUserId = userId.toString().split(':')[0];
                    
                    let usernameResponse, telegramResponse, regionResponse;
                    
                    try {
                        usernameResponse = await axios.get(`${API_BASE_URL}/current-user/${cleanUserId}`);
                        if (usernameResponse?.data?.username) {
                            setUsername(usernameResponse.data.username);
                        }
                    } catch (error) {
                        console.error('Error fetching username:', error);
                    }
                    
                    const hasContractor = await checkContractorExists(cleanUserId);
                    
                    if (!hasContractor) {
                        await handleAutoLogout();
                        return;
                    }
                    
                    const contractorSuccess = await fetchContractorData(cleanUserId);
                    
                    if (!contractorSuccess) {
                        await handleAutoLogout();
                        return;
                    }
                    
                    try {
                        telegramResponse = await axios.get(`${API_BASE_URL}/user-telegram-link/${cleanUserId}`);
                        if (telegramResponse?.data?.group_name) {
                            setTelegramLink(telegramResponse.data.group_name);
                        }
                    } catch (error) {
                        console.error('Error fetching telegram link:', error);
                    }
                    
                    try {
                        regionResponse = await axios.get(`${API_BASE_URL}/user-region/${cleanUserId}`);
                        if (regionResponse?.data?.region) {
                            setLanguage(regionResponse.data.region);
                            
                            try {
                                const translationsResponse = await axios.get(
                                    `${API_BASE_URL}/translations/header/${regionResponse.data.region}`
                                );
                                if (translationsResponse?.data) {
                                    setTranslations({
                                        ...translations,
                                        ...translationsResponse.data
                                    });
                                }
                            } catch (error) {
                                console.error('Error fetching translations:', error);
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching region:', error);
                    }
                    
                    await fetchUserRole(cleanUserId);
                    setDarkTheme();
                    setIsLoggedIn(true);
                    
                } catch (err) {
                    console.error('Error fetching data:', err);
                    if (err.response && err.response.status === 401) {
                        if (!autoLogoutTriggered) {
                            await logout();
                            setIsLoggedIn(false);
                            navigate('/Login');
                        }
                    } else {
                        setGeneralError(language === 'pl-PL'
                            ? 'Błąd podczas pobierania danych użytkownika'
                            : 'Помилка при отриманні даних користувача');
                    }
                }
            }
            setLoading(false);
        };
        
        fetchData();
    }, [user, navigate, logout]);

    useEffect(() => {
        setDarkTheme();
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
        if (!menuOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('menu-open');
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('menu-open');
        }
    };

    const closeMenu = () => {
        setMenuOpen(false);
        document.body.style.overflow = '';
        document.body.classList.remove('menu-open');
        setAdditionalInfoOpen(false);
    };

    const handleLogout = async () => {
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userTheme');
        setUsername("Користувач");
        setDealerName("");
        setIsLoggedIn(false);
        setMenuOpen(false);
        await logout();
        navigate('/Login');
        document.body.style.overflow = '';
        document.body.classList.remove('menu-open');
    };

    const handleLogoClick = () => {
        navigate('/');
        closeMenu();
    };

    const handleNavigation = (path) => {
        navigate(path);
        closeMenu();
    };

    const toggleAdditionalInfo = () => {
        setAdditionalInfoOpen(!additionalInfoOpen);
    };

    
    const openPdfFile = (folderName) => {
        axios.get(`${API_BASE_URL}/api/files/folder/${encodeURIComponent(folderName)}`)
            .then(response => {
                if (response.data.success && response.data.data.length > 0) {
                    const pdfFile = response.data.data.find(f => f.name.toLowerCase().endsWith('.pdf'));
                    if (pdfFile) {
                        window.open(
                            `${API_BASE_URL}/api/files/view/${encodeURIComponent(folderName)}/${encodeURIComponent(pdfFile.name)}`, 
                            '_blank'
                        );
                    } else {
                        alert('PDF файл не знайдено в папці "' + folderName + '"');
                    }
                } else {
                    alert('В папці "' + folderName + '" немає файлів');
                }
            })
            .catch(err => {
                console.error('Error fetching files:', err);
                alert('Помилка при відкритті файлу');
            });
        closeMenu();
    };

    if (loading) {
        return <div className="loading-screen" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>Завантаження...</div>;
    }

    if (generalError) {
        return <div className="error-screen" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>{generalError}</div>;
    }

    return (
        <>
            <header className={`responsive-header ${menuOpen ? "menu-open" : ""}`}>
                <div className="header-container">
                    <div className="header-left">
                        {isLoggedIn && (
                            <button className="menu-toggle" onClick={toggleMenu} aria-label="Menu">
                                <FaBars />
                            </button>
                        )}
                    </div>
                    
                    <div className="header-center" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                        {logoUrl && isLoggedIn && (
                            <img 
                                src={logoUrl} 
                                alt="Logo" 
                                className="header-logo"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        )}
                        {!isLoggedIn && (
                            <h2 style={{ color: '#ffffff' }}>Система дилера</h2>
                        )}
                    </div>
                    
                    <div className="header-right">
                        {isLoggedIn && (
                            <button 
                                className="logout-button" 
                                onClick={handleLogout}
                                aria-label="Вийти"
                            >
                                <FaSignOutAlt />
                            </button>
                        )}
                        {!isLoggedIn && (
                            <a href="/Login" className="login-link" style={{ color: '#ffffff' }}>Login</a>
                        )}
                    </div>
                    
                    <div className={`mobile-menu-overlay ${menuOpen ? "active" : ""}`} onClick={closeMenu}></div>
                    
                    <nav className={`main-nav ${menuOpen ? "active" : ""}`} ref={navRef}>
                        <button className="close-menu-button" onClick={closeMenu} aria-label="Close menu">
                            <FaTimes />
                        </button>
                        <ul className="nav-links">
                            <li>
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigation('/UserProfile');
                                }}>
                                    <span>Профіль</span>
                                </a>
                            </li>
                            <li>
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigation('/DutyPage');
                                }}>
                                    <span>Нове замовлення</span>
                                </a>
                            </li>
                            <li>
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    handleNavigation('/MyOrdersPage');
                                }}>
                                    <span>Мої замовлення</span>
                                </a>
                            </li>
                            <li className="has-submenu">
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    toggleAdditionalInfo();
                                }}>
                                    <span>Додаткова інформація</span>
                                    {additionalInfoOpen ? <FaChevronUp className="submenu-icon" /> : <FaChevronDown className="submenu-icon" />}
                                </a>
                                {additionalInfoOpen && (
                                    <ul className="submenu">
                                        <li>
                                            <a 
                                                href="https://rolis.ua/cooperation-conditions.php?lang=1" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    closeMenu();
                                                }}
                                            >
                                                <span className="bullet">•</span> Умови співпраці
                                            </a>
                                        </li>
                                        <li>
                                            {}
                                            <a 
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openPdfFile('Залишки тканин');
                                                }}
                                            >
                                                <span className="bullet">•</span> Залишки тканин
                                            </a>
                                        </li>
                                        <li>
                                            {}
                                            <a 
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openPdfFile('Повна випродаж');
                                                }}
                                            >
                                                <span className="bullet">•</span> Повна випродаж
                                            </a>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        </ul>
                        
                        <div className="menu-footer">
                            <div className="menu-footer-title">Роліс | Контакти</div>
                            <div className="menu-footer-contacts">
                                <div className="contact-item">
                                    <FaPhone className="contact-icon" />
                                    <span>+38 (050) 434 14 30</span>
                                </div>
                                <div className="contact-item">
                                    <FaEnvelope className="contact-icon" />
                                    <span>orders@rolis.ua</span>
                                </div>
                            </div>
                        </div>
                    </nav>
                </div>
            </header>
            <div className="content-spacer"></div>
        </>
    );
}

export default Header;
